// Generate human-readable calculation explanations

export function explainLifePath(day, month, year) {
  const dayStr = parseInt(day);
  const monthStr = parseInt(month);
  const yearStr = parseInt(year);
  
  const dayReduced = reduceForDisplay(dayStr);
  const monthReduced = reduceForDisplay(monthStr);
  const yearReduced = reduceForDisplay(yearStr);
  
  const sum = dayReduced.final + monthReduced.final + yearReduced.final;
  const finalReduction = reduceForDisplay(sum);
  
  let explanation = `**Gimimo data:** ${day}/${month}/${year}\n\n`;
  explanation += `**Žingsnis 1 - Sumažiname kiekvieną komponentą:**\n`;
  explanation += `• Diena: ${day}${dayReduced.steps ? ` → ${dayReduced.steps} = ${dayReduced.final}` : ` = ${dayReduced.final}`}\n`;
  explanation += `• Mėnuo: ${month}${monthReduced.steps ? ` → ${monthReduced.steps} = ${monthReduced.final}` : ` = ${monthReduced.final}`}\n`;
  explanation += `• Metai: ${year} → ${getYearSteps(year)} = ${yearReduced.final}\n\n`;
  
  explanation += `**Žingsnis 2 - Sudedame rezultatus:**\n`;
  explanation += `${dayReduced.final} + ${monthReduced.final} + ${yearReduced.final} = ${sum}\n\n`;
  
  if (finalReduction.steps) {
    explanation += `**Žingsnis 3 - Galutinis sumažinimas:**\n`;
    explanation += `${sum} → ${finalReduction.steps} = **${finalReduction.final}**`;
  } else {
    explanation += `**Galutinis skaičius: ${finalReduction.final}**`;
  }
  
  return explanation;
}

export function explainExpression(fullName) {
  const LITHUANIAN_LETTER_MAP = {
    'A': 1, 'Ą': 1, 'J': 1, 'S': 1, 'Š': 1,
    'a': 1, 'ą': 1, 'j': 1, 's': 1, 'š': 1,
    'B': 2, 'K': 2, 'T': 2,
    'b': 2, 'k': 2, 't': 2,
    'C': 3, 'Č': 3, 'L': 3, 'U': 3, 'Ų': 3, 'Ū': 3,
    'c': 3, 'č': 3, 'l': 3, 'u': 3, 'ų': 3, 'ū': 3,
    'D': 4, 'M': 4, 'V': 4,
    'd': 4, 'm': 4, 'v': 4,
    'E': 5, 'Ę': 5, 'Ė': 5, 'N': 5,
    'e': 5, 'ę': 5, 'ė': 5, 'n': 5,
    'F': 6, 'O': 6,
    'f': 6, 'o': 6,
    'G': 7, 'P': 7, 'Y': 7,
    'g': 7, 'p': 7, 'y': 7,
    'H': 8, 'Z': 8, 'Ž': 8,
    'h': 8, 'z': 8, 'ž': 8,
    'I': 9, 'Į': 9, 'R': 9,
    'i': 9, 'į': 9, 'r': 9,
  };
  
  const cleanName = fullName.replace(/[^A-ZĄČĘĖĮŠŲŪŽa-ząčęėįšųūž]/g, '');
  let letterBreakdown = [];
  let sum = 0;
  
  for (let char of cleanName) {
    if (LITHUANIAN_LETTER_MAP[char]) {
      const value = LITHUANIAN_LETTER_MAP[char];
      letterBreakdown.push(`${char}(${value})`);
      sum += value;
    }
  }
  
  const reduction = reduceForDisplay(sum);
  
  let explanation = `**Pilnas vardas:** ${fullName}\n\n`;
  explanation += `**Raidžių vertės:**\n`;
  explanation += letterBreakdown.join(' + ') + ` = ${sum}\n\n`;
  
  if (reduction.steps) {
    explanation += `**Sumažinimas:**\n`;
    explanation += `${sum} → ${reduction.steps} = **${reduction.final}**`;
  } else {
    explanation += `**Galutinis skaičius: ${reduction.final}**`;
  }
  
  return explanation;
}

export function explainSoul(fullName) {
  const VOWELS = ['A', 'Ą', 'E', 'Ę', 'Ė', 'I', 'Į', 'U', 'Ų', 'Ū', 'O', 'Y', 'a', 'ą', 'e', 'ę', 'ė', 'i', 'į', 'u', 'ų', 'ū', 'o', 'y'];
  const LITHUANIAN_LETTER_MAP = {
    'A': 1, 'Ą': 1, 'a': 1, 'ą': 1,
    'E': 5, 'Ę': 5, 'Ė': 5, 'e': 5, 'ę': 5, 'ė': 5,
    'I': 9, 'Į': 9, 'i': 9, 'į': 9,
    'U': 3, 'Ų': 3, 'Ū': 3, 'u': 3, 'ų': 3, 'ū': 3,
    'O': 6, 'o': 6,
    'Y': 7, 'y': 7
  };
  
  const cleanName = fullName.replace(/[^A-ZĄČĘĖĮŠŲŪŽa-ząčęėįšųūž]/g, '');
  let vowelBreakdown = [];
  let sum = 0;
  
  for (let char of cleanName) {
    if (VOWELS.includes(char) && LITHUANIAN_LETTER_MAP[char]) {
      const value = LITHUANIAN_LETTER_MAP[char];
      vowelBreakdown.push(`${char}(${value})`);
      sum += value;
    }
  }
  
  const reduction = reduceForDisplay(sum);
  
  let explanation = `**Pilnas vardas:** ${fullName}\n\n`;
  explanation += `**Balsės (A, Ą, E, Ę, Ė, I, Į, U, Ų, Ū, O, Y):**\n`;
  explanation += vowelBreakdown.join(' + ') + ` = ${sum}\n\n`;
  
  if (reduction.steps) {
    explanation += `**Sumažinimas:**\n`;
    explanation += `${sum} → ${reduction.steps} = **${reduction.final}**`;
  } else {
    explanation += `**Galutinis skaičius: ${reduction.final}**`;
  }
  
  return explanation;
}

export function explainPersonality(fullName) {
  const VOWELS = ['A', 'Ą', 'E', 'Ę', 'Ė', 'I', 'Į', 'U', 'Ų', 'Ū', 'O', 'Y', 'a', 'ą', 'e', 'ę', 'ė', 'i', 'į', 'u', 'ų', 'ū', 'o', 'y'];
  const LITHUANIAN_LETTER_MAP = {
    'J': 1, 'S': 1, 'Š': 1, 'j': 1, 's': 1, 'š': 1,
    'B': 2, 'K': 2, 'T': 2, 'b': 2, 'k': 2, 't': 2,
    'C': 3, 'Č': 3, 'L': 3, 'c': 3, 'č': 3, 'l': 3,
    'D': 4, 'M': 4, 'V': 4, 'd': 4, 'm': 4, 'v': 4,
    'N': 5, 'n': 5,
    'F': 6, 'f': 6,
    'G': 7, 'P': 7, 'g': 7, 'p': 7,
    'H': 8, 'Z': 8, 'Ž': 8, 'h': 8, 'z': 8, 'ž': 8,
    'R': 9, 'r': 9
  };
  
  const cleanName = fullName.replace(/[^A-ZĄČĘĖĮŠŲŪŽa-ząčęėįšųūž]/g, '');
  let consonantBreakdown = [];
  let sum = 0;
  
  for (let char of cleanName) {
    if (!VOWELS.includes(char) && LITHUANIAN_LETTER_MAP[char]) {
      const value = LITHUANIAN_LETTER_MAP[char];
      consonantBreakdown.push(`${char}(${value})`);
      sum += value;
    }
  }
  
  const reduction = reduceForDisplay(sum);
  
  let explanation = `**Pilnas vardas:** ${fullName}\n\n`;
  explanation += `**Priebalsės:**\n`;
  explanation += consonantBreakdown.join(' + ') + ` = ${sum}\n\n`;
  
  if (reduction.steps) {
    explanation += `**Sumažinimas:**\n`;
    explanation += `${sum} → ${reduction.steps} = **${reduction.final}**`;
  } else {
    explanation += `**Galutinis skaičius: ${reduction.final}**`;
  }
  
  return explanation;
}

// Helper functions
function reduceForDisplay(num) {
  const original = num;
  const steps = [];
  
  // Check for master numbers
  if (num === 11 || num === 22 || num === 33 || num === 44) {
    return { final: num, steps: null };
  }
  
  while (num > 9) {
    const digits = num.toString().split('').map(d => parseInt(d));
    steps.push(digits.join('+'));
    num = digits.reduce((sum, digit) => sum + digit, 0);
    
    if (num === 11 || num === 22 || num === 33 || num === 44) {
      return { final: num, steps: steps.join(' = ') };
    }
  }
  
  if (steps.length > 0) {
    return { final: num, steps: steps.join(' = ') };
  }
  
  return { final: num, steps: null };
}

function getYearSteps(year) {
  const digits = year.toString().split('').map(d => parseInt(d));
  let sum = digits.reduce((acc, d) => acc + d, 0);
  let steps = [digits.join('+')];
  
  while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33 && sum !== 44) {
    const newDigits = sum.toString().split('').map(d => parseInt(d));
    steps.push(newDigits.join('+'));
    sum = newDigits.reduce((acc, d) => acc + d, 0);
  }
  
  return steps.join(' = ');
}
